import { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Tab } from '@headlessui/react';
import cx from 'classnames';
import Head from 'next/head';

import { Icon, Link, Spinner } from 'components';
import { getLayout } from 'components/layouts';
import { ButtonV3 } from 'components/ComponentV2';
import {
  NewPostModal,
  Post,
  DiscussionsSettingsModal,
  StartDiscussion,
  PendingPostsBanner,
  PendingPosts,
  PostModal,
  DiscussionsMedia,
  DiscussionsDocs,
} from 'components/discussions';
import { withAuth } from 'lib/hocs/withAuth';
import { PAGE_ROUTES } from 'lib/page-routes';
import { DiscussionsProvider, useDiscussions } from 'lib/providers/DiscussionsProvider';
import { DiscussionProvider } from 'lib/providers/DiscussionProvider';
import { useAuth } from 'lib/providers/AuthProvider';

import styles from './discussions.module.scss';

const Header: React.FC<{ scrolled: boolean }> = ({ scrolled }) => {
  const [t] = useTranslation('discussions');
  const { openNewPostModal, openSettingsModal, showPendingPosts } = useDiscussions();

  if (showPendingPosts) return null;

  return (
    <>
      <div className={styles.top}>
        <Link href={PAGE_ROUTES.DASHBOARD} className={styles.breadcrumb}>
          <Icon iconName="chevron-left" size="medium" />
          <p>{t('header.back-to-home')}</p>
        </Link>
      </div>
      <div className={cx(styles.bottom, { [styles.sticked]: scrolled })}>
        <h1 className={styles.title}>{t('header.title')}</h1>
        <div className={styles['actions']}>
          <ButtonV3 color="secondary" borderless={!scrolled} onClick={openSettingsModal}>
            {t('header.actions.settings')}
          </ButtonV3>
          <ButtonV3 onClick={openNewPostModal}>{t('header.actions.new-post')}</ButtonV3>
        </div>
      </div>
    </>
  );
};

const EmptyState = (): ReactElement => {
  const [t] = useTranslation('discussions');
  const { openNewPostModal } = useDiscussions();

  return (
    <section className={styles['empty-state']}>
      <Icon iconName="desert" size="auto" />
      <p className={styles['title']}>{t('no-content.posts.title')}</p>
      <p className={styles['subtitle']}>{t('no-content.posts.subtitle')}</p>
      <ButtonV3 className={styles['new-post']} size="xlarge" onClick={openNewPostModal}>
        {t('no-content.posts.new-post')}
      </ButtonV3>
    </section>
  );
};

const TabContent = (): ReactElement => {
  const [t] = useTranslation('discussions');
  const { posts } = useDiscussions();

  return (
    <section className={styles['tab-content']}>
      <Tab.Group>
        <Tab.List className={styles['tab-list']}>
          <Tab className={styles['tab']}>{t('tabs.0')}</Tab>
          <Tab className={styles['tab']}>{t('tabs.1')}</Tab>
          <Tab className={styles['tab']}>{t('tabs.2')}</Tab>
        </Tab.List>
        <Tab.Panels className={styles['tab-panels']}>
          <Tab.Panel className={styles['tab-panel']}>
            <PendingPostsBanner className={styles['pending-posts']} />
            <StartDiscussion placeholder={t('new-discussion.placeholder')} />
            {posts.map((post) => (
              <Post key={post.id} post={post} className={styles.post} />
            ))}
          </Tab.Panel>
          <Tab.Panel className={styles['tab-panel']}>
            <DiscussionsMedia />
          </Tab.Panel>
          <Tab.Panel className={styles['tab-panel']}>
            <DiscussionsDocs />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </section>
  );
};

const PageContent = (): ReactElement => {
  const { posts, isPostsLoading, pendingPosts, showPendingPosts } = useDiscussions();

  if (isPostsLoading) {
    return <Spinner className={styles.spinner} size="medium" />;
  }

  if (posts.length === 0 && pendingPosts.length === 0) {
    return <EmptyState />;
  }

  if (showPendingPosts) {
    return <PendingPosts />;
  }

  return <TabContent />;
};

const Discussions: React.FC = () => {
  const { managerSociety } = useAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerElement = containerRef.current;
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    function handler() {
      if (!containerElement) return;
      setScrolled(containerElement.scrollTop >= 40);
    }

    containerElement?.addEventListener('scroll', handler);

    return () => {
      containerElement?.removeEventListener('scroll', handler);
    };
  }, [containerElement]);

  if (!managerSociety) return null;

  return (
    <>
      <Head>
        <title>Discussions | Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>
      <section className={styles.container} ref={containerRef}>
        <DiscussionsProvider studentSociety={managerSociety} isManagerPage={true}>
          <DiscussionProvider>
            <Header scrolled={scrolled} />
            <PageContent />
            <PostModal containerClassName={styles['discussion-modal']} />
            <NewPostModal containerClassName={styles['discussion-modal']} />
            <DiscussionsSettingsModal containerClassName={styles['discussion-modal']} />
          </DiscussionProvider>
        </DiscussionsProvider>
      </section>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'discussions'])),
    },
  };
}

const DiscussionsWrapper = withAuth(Discussions);

// @ts-ignore
DiscussionsWrapper.getLayout = getLayout;

export default DiscussionsWrapper;
